import { useMemo, useState, useRef, useCallback, useEffect } from 'react';

const QUOTES = [
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Quality means doing it right when no one is looking.", author: "Henry Ford" },
  { text: "A satisfied customer is the best business strategy of all.", author: "Michael LeBoeuf" },
  { text: "Excellence is not a skill — it's an attitude.", author: "Ralph Marston" },
  { text: "Every wrap tells a story. Make yours one worth telling.", author: "WrapMind" },
  { text: "Do good work. The rest takes care of itself.", author: "Shop wisdom" },
  { text: "The bitterness of poor quality remains long after the sweetness of low price is forgotten.", author: "Benjamin Franklin" },
  { text: "We are what we repeatedly do. Excellence, then, is not an act but a habit.", author: "Aristotle" },
  { text: "Make each day your masterpiece.", author: "John Wooden" },
  { text: "Your craft is your signature. Make it worthy of the work.", author: "WrapMind" },
  { text: "Speed is fine, but accuracy is final.", author: "Wyatt Earp" },
  { text: "Take care of your customers and they'll take care of your business.", author: "Ray Kroc" },
  { text: "Success is not final, failure is not fatal — it is the courage to continue that counts.", author: "Winston Churchill" },
  { text: "The customer's perception is your reality.", author: "Kate Zabriskie" },
  { text: "Details make perfection, and perfection is not a detail.", author: "Leonardo da Vinci" },
  { text: "The difference between ordinary and extraordinary is that little extra.", author: "Jimmy Johnson" },
  { text: "Work hard in silence. Let success make the noise.", author: "Frank Ocean" },
  { text: "A dream doesn't become reality through magic — it takes sweat, determination, and hard work.", author: "Colin Powell" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { text: "Good, better, best. Never let it rest until your good is better and your better is best.", author: "Tim Duncan" },
  { text: "Hustle beats talent when talent doesn't hustle.", author: "Ross Simmonds" },
  { text: "Don't count the days. Make the days count.", author: "Muhammad Ali" },
  { text: "You don't build a business. You build people, and people build the business.", author: "Zig Ziglar" },
  { text: "Do or do not. There is no try.", author: "Yoda" },
  { text: "The harder I work, the luckier I seem to have.", author: "Samuel Goldwyn" },
  { text: "An ounce of performance is worth pounds of promises.", author: "Mae West" },
  { text: "Strive not to be a success, but rather to be of value.", author: "Albert Einstein" },
  { text: "I find that the harder I work, the more luck I seem to have.", author: "Thomas Jefferson" },
  { text: "Opportunity is missed by most people because it is dressed in overalls and looks like work.", author: "Thomas Edison" },
  { text: "It's not about having time. It's about making time.", author: "Unknown" },
  { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
];

const DAY_ABBR = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// ── Insight engine ──────────────────────────────────────────────────────────────
function buildInsights() {
  try {
    const now = new Date();
    const nowMs = now.getTime();
    const day7Ms = 7 * 24 * 60 * 60 * 1000;
    const day30Ms = 30 * 24 * 60 * 60 * 1000;

    const invoices = JSON.parse(localStorage.getItem('wm-invoices-v1') || '[]');
    const estimates = JSON.parse(localStorage.getItem('wm-estimates-v2') || '[]');
    const leads = JSON.parse(localStorage.getItem('wm-leads-v1') || '[]');
    const workOrders = JSON.parse(localStorage.getItem('wm-work-orders-v1') || '[]');
    const scheduling = JSON.parse(localStorage.getItem('wm-scheduling-v1') || '[]');

    const insights = [];

    // ── 1. Revenue trend ────────────────────────────────────────────────────────
    const thisWeekRev = invoices
      .filter(inv => inv.paidAt && nowMs - new Date(inv.paidAt).getTime() < day7Ms)
      .reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0);
    const lastWeekRev = invoices
      .filter(inv => {
        if (!inv.paidAt) return false;
        const age = nowMs - new Date(inv.paidAt).getTime();
        return age >= day7Ms && age < 2 * day7Ms;
      })
      .reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0);

    if (thisWeekRev > 0 || lastWeekRev > 0) {
      const pct = lastWeekRev > 0 ? Math.round((thisWeekRev - lastWeekRev) / lastWeekRev * 100) : null;
      const up = pct === null ? null : pct >= 0;
      insights.push({
        id: 'revenue-trend',
        icon: up === false ? '📉' : '📈',
        label: 'Revenue Trend',
        headline: pct === null
          ? `$${thisWeekRev.toLocaleString('en-US', { maximumFractionDigits: 0 })} collected this week`
          : up
            ? `Revenue up ${pct}% this week`
            : `Revenue down ${Math.abs(pct)}% this week`,
        detail: pct === null
          ? 'No comparison data yet — keep pushing!'
          : up
            ? `$${thisWeekRev.toLocaleString('en-US', { maximumFractionDigits: 0 })} vs $${lastWeekRev.toLocaleString('en-US', { maximumFractionDigits: 0 })} last week. Strong momentum — capitalize on it.`
            : `$${thisWeekRev.toLocaleString('en-US', { maximumFractionDigits: 0 })} vs $${lastWeekRev.toLocaleString('en-US', { maximumFractionDigits: 0 })} last week. Consider following up on open estimates.`,
        accent: up === false ? '#ef4444' : '#22c55e',
      });
    }

    // ── 2. Pipeline health ─────────────────────────────────────────────────────
    const sentEstimates = estimates.filter(e => e.status === 'sent');
    const oldSent = sentEstimates.filter(e => e.sentAt && nowMs - new Date(e.sentAt).getTime() > day7Ms);
    if (sentEstimates.length > 0) {
      insights.push({
        id: 'pipeline',
        icon: '🔁',
        label: 'Pipeline Health',
        headline: `${sentEstimates.length} estimate${sentEstimates.length > 1 ? 's' : ''} awaiting reply`,
        detail: oldSent.length > 0
          ? `${oldSent.length} sent over a week ago. A quick follow-up now could close real money this week.`
          : 'All estimates sent recently. Strike while the iron is hot!',
        accent: oldSent.length > 0 ? '#f59e0b' : '#2E8BF0',
      });
    }

    // ── 3. Conversion rate ─────────────────────────────────────────────────────
    const totalEst = estimates.length;
    const converted = estimates.filter(e => e.status === 'converted').length;
    if (totalEst >= 3) {
      const rate = Math.round(converted / totalEst * 100);
      const benchmark = 35; // industry avg
      insights.push({
        id: 'conversion',
        icon: rate >= benchmark ? '🎯' : '💡',
        label: 'Conversion Rate',
        headline: `${rate}% estimate close rate`,
        detail: rate >= benchmark
          ? `Your ${rate}% close rate beats the industry average of ~35%. Keep delivering value upfront.`
          : `At ${rate}%, there's room to grow past the ~35% industry average. Consider personalizing your follow-ups.`,
        accent: rate >= benchmark ? '#22c55e' : '#f59e0b',
      });
    }

    // ── 4. Lead velocity ───────────────────────────────────────────────────────
    const recentLeads = leads.filter(l => l.createdAt && nowMs - new Date(l.createdAt).getTime() < day30Ms);
    const olderLeads = leads.filter(l => l.createdAt && nowMs - new Date(l.createdAt).getTime() >= day30Ms && nowMs - new Date(l.createdAt).getTime() < 2 * day30Ms);
    if (recentLeads.length > 0 || olderLeads.length > 0) {
      const growing = recentLeads.length >= olderLeads.length;
      insights.push({
        id: 'leads',
        icon: growing ? '🚀' : '📋',
        label: 'Lead Velocity',
        headline: `${recentLeads.length} new lead${recentLeads.length !== 1 ? 's' : ''} this month`,
        detail: growing
          ? `Up from ${olderLeads.length} last month. Your pipeline is growing — now focus on fast qualification.`
          : `Down from ${olderLeads.length} last month. Consider activating a referral or marketing push.`,
        accent: growing ? '#22c55e' : '#f59e0b',
      });
    }

    // ── 5. Active work orders ──────────────────────────────────────────────────
    const activeWo = workOrders.filter(wo => ['active', 'in_progress', 'open'].includes(wo.status));
    if (activeWo.length > 0) {
      insights.push({
        id: 'work-orders',
        icon: '🛠',
        label: 'Shop Floor',
        headline: `${activeWo.length} active job${activeWo.length > 1 ? 's' : ''} in progress`,
        detail: activeWo.length > 3
          ? `Busy shop! ${activeWo.length} jobs running simultaneously. Prioritize communication to keep clients happy.`
          : `${activeWo.length} job${activeWo.length > 1 ? 's' : ''} on the floor. Solid pace — keep quality front of mind.`,
        accent: '#2E8BF0',
      });
    }

    // ── 6. Scheduling load ─────────────────────────────────────────────────────
    const upcoming7 = scheduling.filter(appt => {
      if (!appt.date || appt.status === 'cancelled') return false;
      const d = new Date(appt.date + 'T12:00:00').getTime();
      return d >= nowMs && d < nowMs + day7Ms;
    });
    if (upcoming7.length > 0) {
      insights.push({
        id: 'scheduling',
        icon: '📅',
        label: 'Schedule',
        headline: `${upcoming7.length} appointment${upcoming7.length > 1 ? 's' : ''} next 7 days`,
        detail: upcoming7.length >= 5
          ? 'Packed week ahead. Make sure every slot is prepped and customers are confirmed.'
          : 'Light schedule ahead — good time to push for bookings or tackle backlog projects.',
        accent: '#8b5cf6',
      });
    }

    // ── 7. Outstanding invoices ────────────────────────────────────────────────
    const outstanding = invoices.filter(inv => ['sent', 'partial', 'overdue'].includes(inv.status));
    const outstandingTotal = outstanding.reduce((s, inv) => s + (Number(inv.amountDue) || 0), 0);
    if (outstanding.length > 0 && outstandingTotal > 0) {
      const overdue = invoices.filter(inv => inv.status === 'overdue');
      insights.push({
        id: 'outstanding',
        icon: overdue.length > 0 ? '⚠️' : '💰',
        label: 'Receivables',
        headline: `$${outstandingTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })} outstanding`,
        detail: overdue.length > 0
          ? `${overdue.length} overdue invoice${overdue.length > 1 ? 's' : ''} — collecting now protects your cash flow.`
          : `${outstanding.length} invoice${outstanding.length > 1 ? 's' : ''} sent and awaiting payment. Follow-up timing is everything.`,
        accent: overdue.length > 0 ? '#ef4444' : '#f59e0b',
      });
    }

    // ── 8. Best performing day (last 30d) ──────────────────────────────────────
    const dayTotals = {};
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    invoices
      .filter(inv => inv.paidAt && nowMs - new Date(inv.paidAt).getTime() < day30Ms)
      .forEach(inv => {
        const dayIdx = new Date(inv.paidAt).getDay();
        dayTotals[dayIdx] = (dayTotals[dayIdx] || 0) + (Number(inv.amountPaid) || 0);
      });
    const dayEntries = Object.entries(dayTotals);
    if (dayEntries.length >= 3) {
      const best = dayEntries.sort((a, b) => b[1] - a[1])[0];
      insights.push({
        id: 'best-day',
        icon: '⭐',
        label: 'Peak Day',
        headline: `${dayNames[best[0]]}s are your strongest`,
        detail: `$${Number(best[1]).toLocaleString('en-US', { maximumFractionDigits: 0 })} collected on ${dayNames[best[0]]}s in the last 30 days. Consider scheduling your most valuable appointments then.`,
        accent: '#f59e0b',
      });
    }

    // Fallback if no data at all
    if (insights.length === 0) {
      insights.push({
        id: 'getting-started',
        icon: '✨',
        label: 'Getting Started',
        headline: 'Your AI insights are warming up',
        detail: 'As you add estimates, invoices, and appointments, this panel will surface trends, opportunities, and recommendations tailored to your shop.',
        accent: '#2E8BF0',
      });
    }

    return insights;
  } catch {
    return [{
      id: 'error',
      icon: '✨',
      label: 'AI Insights',
      headline: 'Insights ready when your data is',
      detail: 'Add some estimates and invoices to see personalized shop analytics here.',
      accent: '#2E8BF0',
    }];
  }
}

// ── AI Insights Carousel ────────────────────────────────────────────────────────
function AIInsightsCarousel() {
  const insights = useMemo(() => buildInsights(), []);
  const [current, setCurrent] = useState(0);
  const [animDir, setAnimDir] = useState(null); // 'left' | 'right' | null
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);
  const total = insights.length;

  const goTo = useCallback((nextIdx, dir) => {
    if (nextIdx === current) return;
    setAnimDir(dir);
    setVisible(false);
    setTimeout(() => {
      setCurrent(nextIdx);
      setAnimDir(dir === 'left' ? 'from-right' : 'from-left');
      setVisible(true);
      // reset after animation
      setTimeout(() => setAnimDir(null), 350);
    }, 180);
  }, [current]);

  const advance = useCallback(() => {
    goTo((current + 1) % total, 'left');
  }, [current, total, goTo]);

  const retreat = useCallback(() => {
    goTo((current - 1 + total) % total, 'right');
  }, [current, total, goTo]);

  // Auto-advance every 10 seconds
  useEffect(() => {
    timerRef.current = setInterval(advance, 10000);
    return () => clearInterval(timerRef.current);
  }, [advance]);

  // Reset timer on manual nav
  const manualNav = useCallback((fn) => {
    clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(advance, 10000);
  }, [advance]);

  const slide = insights[current];

  // Slide animation class
  let translateClass = 'translate-x-0 opacity-100';
  if (!visible && animDir === 'left') translateClass = '-translate-x-3 opacity-0';
  if (!visible && animDir === 'right') translateClass = 'translate-x-3 opacity-0';
  if (visible && animDir === 'from-right') translateClass = 'translate-x-0 opacity-100';
  if (visible && animDir === 'from-left') translateClass = 'translate-x-0 opacity-100';

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {/* Spark icon */}
      <div
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ background: `${slide.accent}22`, border: `1px solid ${slide.accent}44` }}
      >
        <span className="text-[var(--wm-text-body)] leading-none">{slide.icon}</span>
      </div>

      {/* Text content */}
      <div
        className="flex-1 min-w-0 overflow-hidden"
        style={{
          transition: 'transform 0.18s ease-out, opacity 0.18s ease-out',
          transform: !visible ? (animDir === 'left' ? 'translateX(-10px)' : 'translateX(10px)') : 'translateX(0)',
          opacity: visible ? 1 : 0,
        }}
      >
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span
            className="font-semibold uppercase tracking-widest flex-shrink-0"
            style={{ fontSize: 'var(--wm-text-micro)', color: slide.accent }}
          >
            {slide.label}
          </span>
          <span
            className="font-semibold truncate text-[#0F1923] dark:text-[#F8FAFE]"
            style={{ fontSize: 'var(--wm-text-body)' }}
          >
            {slide.headline}
          </span>
        </div>
        <p
          className="text-[#64748B] dark:text-[#7D93AE] truncate mt-0.5"
          style={{ fontSize: 'var(--wm-text-meta)' }}
          title={slide.detail}
        >
          {slide.detail}
        </p>
      </div>

      {/* Controls */}
      <div className="flex-shrink-0 flex items-center gap-1 ml-1">
        {/* Dot indicators */}
        <div className="hidden sm:flex items-center gap-0.5 mr-1">
          {insights.map((_, i) => (
            <button
              key={i}
              onClick={() => manualNav(() => goTo(i, i > current ? 'left' : 'right'))}
              className="rounded-full transition-all duration-200"
              style={{
                width: i === current ? '14px' : '5px',
                height: '5px',
                background: i === current ? slide.accent : 'rgba(125,147,174,0.35)',
              }}
              aria-label={`Go to insight ${i + 1}`}
            />
          ))}
        </div>

        {/* Prev */}
        <button
          onClick={() => manualNav(retreat)}
          className="w-6 h-6 rounded-md flex items-center justify-center text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
          style={{ background: 'rgba(125,147,174,0.10)' }}
          aria-label="Previous insight"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Next */}
        <button
          onClick={() => manualNav(advance)}
          className="w-6 h-6 rounded-md flex items-center justify-center text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors"
          style={{ background: 'rgba(125,147,174,0.10)' }}
          aria-label="Next insight"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function DashboardPulse() {
  const data = useMemo(() => {
    try {
      // Today string (no UTC flip)
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      // Time of day
      const h = now.getHours();
      const timeOfDay = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';

      // First name
      const userProfile = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
      const shopProfile = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
      const rawName = userProfile.displayName || userProfile.name || shopProfile.name || '';
      const firstName = rawName.trim().split(/\s+/)[0] || 'there';

      // Revenue data
      const invoices = JSON.parse(localStorage.getItem('wm-invoices-v1') || '[]');
      const nowMs = Date.now();
      const day7Ms = 7 * 24 * 60 * 60 * 1000;

      const weekRevenue = invoices
        .filter(inv => inv.paidAt && nowMs - new Date(inv.paidAt).getTime() < day7Ms)
        .reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0);

      const lastWeekRevenue = invoices
        .filter(inv => {
          if (!inv.paidAt) return false;
          const age = nowMs - new Date(inv.paidAt).getTime();
          return age >= day7Ms && age < 2 * day7Ms;
        })
        .reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0);

      const weekPct = Math.round((weekRevenue - lastWeekRevenue) / (lastWeekRevenue || 1) * 100);

      // Awaiting estimates
      const estimates = JSON.parse(localStorage.getItem('wm-estimates-v2') || '[]');
      const awaitingCount = estimates.filter(
        est => est.status === 'sent' && est.sentAt && new Date(est.sentAt) < new Date(nowMs - day7Ms)
      ).length;

      // Today appointments
      const scheduling = JSON.parse(localStorage.getItem('wm-scheduling-v1') || '[]');
      const todayAppts = scheduling.filter(
        appt => appt.date === todayStr && appt.status !== 'cancelled'
      ).length;

      // Active work orders
      const workOrders = JSON.parse(localStorage.getItem('wm-work-orders-v1') || '[]');
      const activeWoCount = workOrders.filter(
        wo => wo.status === 'active' || wo.status === 'in_progress' || wo.status === 'open'
      ).length;

      // Headline — fluid, time-aware, always fresh
      const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

      const MORNING = [
        (n) => <>Good morning, <strong>{n}</strong>. Your best work starts right now.</>,
        (n) => <>Morning, <strong>{n}</strong>! The day's wide open — go make it count.</>,
        (n) => <>Good morning, <strong>{n}</strong>. Fresh day, fresh opportunities.</>,
        (n) => <>Good morning, <strong>{n}</strong>. Another chance to do something remarkable.</>,
        (n) => <>Morning, <strong>{n}</strong>. The shops that win are already moving — and so are you.</>,
        (n) => <>Good morning, <strong>{n}</strong>. Coffee in hand, goals in mind — let's go.</>,
        (n) => <>Hey <strong>{n}</strong>, good morning! Every great build starts with a great start.</>,
        (n) => <>Good morning, <strong>{n}</strong>. You showed up. That already puts you ahead.</>,
        (n) => <>Morning, <strong>{n}</strong>! Great things don't happen by accident — you're living proof.</>,
        (n) => <>Good morning, <strong>{n}</strong>. Make today the kind of day future-you thanks you for.</>,
        (n) => <>Morning, <strong>{n}</strong>. Your craft speaks for itself — let's give it something great to say.</>,
        (n) => <>Good morning, <strong>{n}</strong>. A new day means new wins waiting to happen.</>,
        (n) => <>Good morning, <strong>{n}</strong>. The world doesn't know what you've got planned — but it will.</>,
        (n) => <>Morning, <strong>{n}</strong>! You bring something to this industry that no one else can.</>,
        (n) => <>Good morning, <strong>{n}</strong>. Consistency builds empires — and you're consistent.</>,
      ];

      const AFTERNOON = [
        (n) => <>Good afternoon, <strong>{n}</strong>. The day's in full swing — keep the momentum going.</>,
        (n) => <>Afternoon, <strong>{n}</strong>! Still plenty of day left to close something great.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. You're doing great — don't stop now.</>,
        (n) => <>Hey <strong>{n}</strong>, good afternoon! Mid-day check-in: you're killing it.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Every quote you send is a door you're opening.</>,
        (n) => <>Afternoon, <strong>{n}</strong>. The best shops don't slow down at noon — and neither do you.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Your work today is someone's dream tomorrow.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Keep building — the results are worth it.</>,
        (n) => <>Afternoon, <strong>{n}</strong>. Mid-day momentum is real — ride it.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Every wrap you complete is a story well told.</>,
        (n) => <>Hey <strong>{n}</strong>, good afternoon! Your reputation is built one job at a time.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Detail, craft, pride — that's what sets your shop apart.</>,
        (n) => <>Afternoon, <strong>{n}</strong>! The pipeline doesn't fill itself. Good thing you're on it.</>,
        (n) => <>Good afternoon, <strong>{n}</strong>. Keep the energy up — the finish line rewards those who do.</>,
        (n) => <>Afternoon, <strong>{n}</strong>. You're building something real. Keep going.</>,
      ];

      const EVENING = [
        (n) => <>Good evening, <strong>{n}</strong>. Whatever got done today was worth doing right.</>,
        (n) => <>Evening, <strong>{n}</strong>. Wind down well — you've earned it.</>,
        (n) => <>Good evening, <strong>{n}</strong>. The best shops never really clock out — they just shift gears.</>,
        (n) => <>Hey <strong>{n}</strong>, good evening! Another day of doing what you love.</>,
        (n) => <>Good evening, <strong>{n}</strong>. Today's work is tomorrow's reputation.</>,
        (n) => <>Evening, <strong>{n}</strong>. Rest well — great work deserves great recovery.</>,
        (n) => <>Good evening, <strong>{n}</strong>. The day may be winding down, but your impact isn't.</>,
        (n) => <>Good evening, <strong>{n}</strong>. Every job you closed today is a customer who'll remember you.</>,
        (n) => <>Evening, <strong>{n}</strong>. Quality work done with pride — that's what today was.</>,
        (n) => <>Hey <strong>{n}</strong>, good evening! Shops like yours are the backbone of the industry.</>,
        (n) => <>Good evening, <strong>{n}</strong>. Tomorrow's another shot — tonight, be proud of today.</>,
        (n) => <>Evening, <strong>{n}</strong>. The best in the business never settle. Neither do you.</>,
        (n) => <>Good evening, <strong>{n}</strong>. Another day, another standard set.</>,
        (n) => <>Good evening, <strong>{n}</strong>. The grind is real, but so are the results.</>,
        (n) => <>Evening, <strong>{n}</strong>. You built something today. That matters.</>,
      ];

      const greetingPool = timeOfDay === 'morning' ? MORNING : timeOfDay === 'afternoon' ? AFTERNOON : EVENING;

      const REVENUE_GREETINGS = [
        (n, rev, pct) => <>Good {timeOfDay}, <strong>{n}</strong>. Your shop pulled in <strong>${rev}</strong> this week{pct !== 0 ? <> — <strong style={{ color: pct > 0 ? '#22c55e' : '#ef4444' }}>{pct > 0 ? '+' : ''}{pct}%</strong> vs last week</> : null}. Keep it up.</>,
        (n, rev, pct) => <>Hey <strong>{n}</strong>! <strong>${rev}</strong> in the bank this week{pct !== 0 ? <> — {pct > 0 ? '+' : ''}{pct}% over last week</> : null}. Nice work.</>,
        (n, rev, pct) => <>Good {timeOfDay}, <strong>{n}</strong>. <strong>${rev}</strong> collected this week{pct > 0 ? <> — up <strong style={{ color: '#22c55e' }}>{pct}%</strong> from last week. Momentum is yours.</> : pct < 0 ? <> — down <strong style={{ color: '#ef4444' }}>{Math.abs(pct)}%</strong>, but there is still time to push.</> : <> — consistent. That is the foundation of great shops.</>}</>,
        (n, rev, pct) => <><strong>{n}</strong>, good {timeOfDay}. This week: <strong>${rev}</strong>{pct !== 0 ? <> ({pct > 0 ? <span style={{ color: '#22c55e' }}>+{pct}%</span> : <span style={{ color: '#ef4444' }}>{pct}%</span>} vs last week)</> : null}. You are running a real business.</>,
      ];

      const revStr = weekRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 });
      const headline = weekRevenue > 0
        ? pick(REVENUE_GREETINGS)(firstName, revStr, weekPct)
        : pick(greetingPool)(firstName);

      // Insight segments
      const segments = [
        awaitingCount > 0 && `${awaitingCount} estimate${awaitingCount > 1 ? 's' : ''} awaiting reply`,
        todayAppts > 0 && `${todayAppts} appointment${todayAppts > 1 ? 's' : ''} today`,
      ].filter(Boolean);

      // Daily quote
      const dayOfYear = Math.floor((nowMs - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
      const quote = QUOTES[dayOfYear % QUOTES.length];

      // 7-day sparkline
      const days7 = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      });

      const dailyRevenue = days7.map(dayStr =>
        invoices
          .filter(inv => inv.paidAt && inv.paidAt.startsWith(dayStr))
          .reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0)
      );

      const allZero = dailyRevenue.every(v => v === 0);
      const chartData = allZero
        ? [3200, 5100, 4400, 6800, 7200, 5600, 8400]
        : dailyRevenue;

      const dayLabels = days7.map(d => DAY_ABBR[new Date(d + 'T12:00:00').getDay()]);

      return {
        firstName, weekRevenue, weekPct, awaitingCount, todayAppts, activeWoCount,
        chartData, allZero, days7, dayLabels, segments, headline, quote,
      };
    } catch {
      return {
        firstName: 'there', weekRevenue: 0, weekPct: 0, awaitingCount: 0,
        todayAppts: 0, activeWoCount: 0,
        chartData: [3200, 5100, 4400, 6800, 7200, 5600, 8400], allZero: true,
        days7: [], dayLabels: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
        segments: [],
        headline: <>Good morning, <strong>there</strong>. Your best work starts right now.</>,
        quote: QUOTES[0],
      };
    }
  }, []);

  const maxVal = Math.max(...data.chartData, 1);

  const containerRef = useRef(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    // Normalize to -1 … +1
    const nx = ((e.clientX - left) / width  - 0.5) * 2;
    const ny = ((e.clientY - top)  / height - 0.5) * 2;
    setParallax({ x: nx, y: ny });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setParallax({ x: 0, y: 0 });
  }, []);

  return (
    <div className="flex flex-col flex-shrink-0">
      {/* ── Main greeting row ─────────────────────────────────────────────────── */}
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="wm-glass relative overflow-hidden border-b px-6 py-5 flex items-center gap-6"
        style={{
          background: 'var(--wm-glass-bg)',
          borderColor: 'var(--wm-glass-border)',
          boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.06)',
        }}
      >
        {/* Dot grid — slowest layer (depth illusion: furthest back) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, var(--btn-primary-bg) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            opacity: 0.05,
            transform: `translate(${parallax.x * 4}px, ${parallax.y * 3}px)`,
            transition: 'transform 0.12s ease-out',
          }}
        />

        {/* Left: text content */}
        <div className="relative flex-1 min-w-0 flex flex-col gap-1">
          <p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] leading-snug" style={{ fontSize: '1.5rem' }}>
            {data.headline}
          </p>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-[18px]">
            {data.segments.length > 0 ? data.segments.join(' · ') : 'All clear — nothing urgent today.'}
          </p>
          <p className="text-[var(--wm-text-meta)] italic text-[#94A3B8] dark:text-[#4A6380] mt-2">
            &ldquo;{data.quote.text}&rdquo; <span className="not-italic">— {data.quote.author}</span>
          </p>
        </div>

        {/* Center: decorative SVG wave — mid-layer (moves faster than dot grid) */}
        <div
          className="hidden md:block flex-shrink-0 opacity-[0.07] dark:opacity-[0.10] pointer-events-none select-none"
          aria-hidden="true"
          style={{
            transform: `translate(${parallax.x * 9}px, ${parallax.y * 6}px)`,
            transition: 'transform 0.08s ease-out',
          }}
        >
          <svg width="120" height="44" viewBox="0 0 120 44" fill="none">
            <path
              d="M0 22 Q15 8 30 22 Q45 36 60 22 Q75 8 90 22 Q105 36 120 22"
              stroke="var(--btn-primary-bg)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M0 30 Q15 16 30 30 Q45 44 60 30 Q75 16 90 30 Q105 44 120 30"
              stroke="var(--btn-primary-bg)"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d="M0 14 Q15 0 30 14 Q45 28 60 14 Q75 0 90 14 Q105 28 120 14"
              stroke="var(--btn-primary-bg)"
              strokeWidth="1"
              fill="none"
            />
          </svg>
        </div>

        {/* Right: 7-day revenue sparkline — foreground layer (subtle counter-shift) */}
        <div
          className="relative flex-shrink-0 flex flex-col items-end gap-0.5"
          style={{
            transform: `translate(${parallax.x * -3}px, ${parallax.y * -2}px)`,
            transition: 'transform 0.15s ease-out',
          }}
        >
          <span className="text-[var(--wm-text-micro)] font-semibold uppercase tracking-widest text-[#7D93AE]">
            7-Day Revenue
          </span>

          <svg viewBox="0 0 168 44" className="w-[168px] h-[44px]">
            {data.chartData.map((val, i) => {
              const barHeight = Math.max(3, (val / maxVal) * 38);
              const x = i * 24 + 2;
              const y = 44 - barHeight;
              const isToday = i === data.chartData.length - 1;
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width={18}
                    height={barHeight}
                    rx={3}
                    fill="var(--btn-primary-bg)"
                    opacity={isToday ? 1 : 0.3}
                  />
                  {isToday && (
                    <circle
                      cx={x + 9}
                      cy={y}
                      r={3}
                      fill="var(--btn-primary-bg)"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Day labels */}
          <div className="flex w-[168px]">
            {data.dayLabels.map((label, i) => (
              <span key={i} className="flex-1 text-center text-[var(--wm-text-micro)] text-[#7D93AE]">
                {label}
              </span>
            ))}
          </div>

          {/* Total label — only when real data */}
          {!data.allZero && (
            <span className="text-[var(--wm-text-micro)] text-[#7D93AE]">
              Total:{' '}
              <strong className="text-[#0F1923] dark:text-[#F8FAFE]">
                ${data.chartData.reduce((a, b) => a + b, 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </strong>
            </span>
          )}
        </div>
      </div>

      {/* ── AI Insights bar ────────────────────────────────────────────────────── */}
      <div
        className="wm-glass-subtle border-b px-6 py-3 flex items-center gap-3 overflow-hidden"
        style={{
          background: 'var(--wm-glass-bg)',
          borderColor: 'var(--wm-glass-border)',
          boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.04)',
          minHeight: '52px',
        }}
      >
        {/* Carousel */}
        <AIInsightsCarousel />
      </div>
    </div>
  );
}
